import { useParams, useLocation, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Loader2, Calendar, Share2 } from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import { fetchAssessment, fetchConfig } from "@/lib/api";

const BAND_COLOR = {
  "High Friction": { bg: "bg-red-50", text: "text-red-700", ring: "ring-red-200", bar: "#EF4444" },
  "Emerging Readiness": { bg: "bg-amber-50", text: "text-amber-700", ring: "ring-amber-200", bar: "#F59E0B" },
  "Operationally Ready": { bg: "bg-blue-50", text: "text-blue-700", ring: "ring-blue-200", bar: "#2563EB" },
  "Transformation Ready": { bg: "bg-emerald-50", text: "text-emerald-700", ring: "ring-emerald-200", bar: "#10B981" },
};

const Results = () => {
  const { id } = useParams();
  const location = useLocation();
  const seededResult = location.state?.result || null;

  const assessmentQuery = useQuery({
    queryKey: ["assessment", id],
    queryFn: () => fetchAssessment(id),
    enabled: !seededResult,
    initialData: seededResult || undefined,
    retry: false,
  });

  const configQuery = useQuery({
    queryKey: ["config"],
    queryFn: fetchConfig,
    retry: false,
  });

  const result = assessmentQuery.data;
  const config = configQuery.data;

  if (!result) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center py-32 text-slate-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading your report…
        </div>
      </div>
    );
  }

  const band = BAND_COLOR[result.readiness_band] || BAND_COLOR["Operationally Ready"];

  const calendlyUrl = config?.calendly_url || "https://calendly.com/the-river-company/discovery-call";

  const handleShare = async () => {
    // /api/r/<id> serves an HTML wrapper with dynamic Open Graph tags (LinkedIn rich preview).
    // Human browsers are immediately redirected to /share/<id>.
    const url = `${window.location.origin}/api/r/${result.id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Share link copied to clipboard", { description: "LinkedIn-ready preview · anonymous snapshot, no personal details." });
    } catch {
      toast.error("Couldn't copy. Link: " + url);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 py-10 lg:py-16">
        {/* Header */}
        <div className="mb-6" data-testid="results-header">
          <div className="text-xs font-semibold tracking-[0.18em] uppercase text-slate-500 mb-2">Enterprise Alignment Readiness</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tighter text-slate-900">Your readiness results</h1>
          <p className="mt-3 text-lg text-slate-600">For {result.lead.name} · {result.lead.organization}</p>
        </div>

        {/* Lead-in copy */}
        <div className="mb-10 p-5 rounded-xl bg-blue-50 border border-blue-100 flex items-start justify-between gap-4 flex-wrap" data-testid="results-leadin">
          <p className="text-base text-slate-800 leading-relaxed flex-1 min-w-[260px]">
            Your readiness results are below. To discuss these findings, schedule a 30-minute Enterprise Alignment Review with The River Company.
          </p>
          <Button onClick={handleShare} data-testid="share-results-btn" variant="outline" className="bg-white border-slate-300 text-slate-700 hover:bg-slate-50 rounded-md shrink-0">
            <Share2 className="w-4 h-4 mr-2" /> Share anonymous snapshot
          </Button>
        </div>

        {/* Top grid: Score + Recommended */}
        <div className="grid lg:grid-cols-3 gap-6 mb-8">
          {/* Overall score */}
          <div className="lg:col-span-1 bg-white border border-slate-200 rounded-2xl p-8">
            <div className="text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">Overall Score</div>
            <div className="flex items-baseline gap-2">
              <span data-testid="overall-score" className="font-display text-6xl font-bold text-slate-900 tracking-tighter">{result.overall_score}</span>
              <span className="text-slate-400 text-xl">/100</span>
            </div>
            <div className={`mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${band.bg} ${band.text} ring-1 ${band.ring} text-sm font-semibold`} data-testid="readiness-band">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: band.bar }} />
              {result.readiness_band}
            </div>
            <div className="mt-6 pt-6 border-t border-slate-100 grid grid-cols-2 gap-4">
              <div>
                <div className="text-[10px] font-semibold tracking-widest uppercase text-emerald-700 mb-1">Strongest</div>
                <div className="text-sm font-medium text-slate-900 leading-tight" data-testid="highest-category">{result.highest_category}</div>
              </div>
              <div>
                <div className="text-[10px] font-semibold tracking-widest uppercase text-amber-700 mb-1">Focus area</div>
                <div className="text-sm font-medium text-slate-900 leading-tight" data-testid="lowest-category">{result.lowest_category}</div>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-slate-100">
              <div className="text-xs text-slate-500 mb-1">Raw score</div>
              <div className="font-mono text-sm text-slate-900">{result.raw_score} / 60</div>
            </div>
          </div>

          {/* Recommended service */}
          <div className="lg:col-span-2 relative overflow-hidden rounded-2xl bg-[#312D44] p-8 text-white">
            <div className="absolute inset-0 opacity-[0.06]" style={{
              backgroundImage: "linear-gradient(to right, rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.4) 1px, transparent 1px)",
              backgroundSize: "32px 32px"
            }} />
            <div className="relative">
              <div className="text-xs font-semibold tracking-widest uppercase text-slate-400 mb-2">Recommended Next Step</div>
              <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-white mb-3" data-testid="recommended-service">{result.recommended_service}</h2>
              <p className="text-slate-300 leading-relaxed max-w-2xl">{result.service_description}</p>
              <div className="mt-4 text-sm text-slate-400">Based on your weakest dimension: <span className="text-white font-medium">{result.lowest_category}</span></div>
              <div className="mt-6 flex flex-wrap gap-3">
                <Button asChild data-testid="cta-book-call-btn" className="bg-white text-slate-900 hover:bg-slate-100 rounded-md px-5 py-5 h-auto font-medium">
                  <a href={calendlyUrl} target="_blank" rel="noopener noreferrer">
                    <Calendar className="w-4 h-4 mr-2" /> Schedule a 30-minute Enterprise Alignment Review
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Category Breakdown */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 mb-8">
          <div className="flex items-baseline justify-between mb-6">
            <h3 className="font-display text-xl font-semibold text-slate-900">Category Breakdown</h3>
            <div className="text-xs text-slate-500 font-mono">Scores out of 5</div>
          </div>
          <div className="space-y-4">
            {result.category_scores.map((c) => {
              const isLowest = c.category === result.lowest_category;
              return (
                <div key={c.category} data-testid={`cat-row-${c.category.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <div className="flex justify-between items-center mb-1.5">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-900">{c.category}</span>
                      {isLowest && <span className="text-[10px] font-semibold tracking-wider uppercase text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">Focus Area</span>}
                    </div>
                    <span className="font-mono text-sm font-semibold text-slate-900">{c.score.toFixed(2)}</span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{ width: `${(c.score / 5) * 100}%`, background: isLowest ? "#F59E0B" : "#2563EB" }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* AI Report */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-10 mb-8">
          <div className="text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">Executive Readiness Report</div>
          <h3 className="font-display text-2xl font-semibold text-slate-900 mb-6">What the numbers mean for you</h3>
          <div className="report-prose max-w-3xl" data-testid="ai-report">
            <ReportRender markdown={result.ai_report} />
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center">
          <Calendar className="w-8 h-8 text-blue-600 mx-auto mb-3" />
          <h3 className="font-display text-2xl font-semibold text-slate-900 mb-2">Talk it through with us</h3>
          <p className="text-slate-600 max-w-xl mx-auto">To discuss these findings, schedule a 30-minute Enterprise Alignment Review with The River Company.</p>
          <div className="mt-6 flex justify-center gap-3 flex-wrap">
            <Button asChild className="bg-[#312D44] hover:bg-[#1F1B30] text-white rounded-md px-5 py-5 h-auto" data-testid="bottom-book-call-btn">
              <a href={calendlyUrl} target="_blank" rel="noopener noreferrer"><Calendar className="w-4 h-4 mr-2" /> Schedule a 30-minute Enterprise Alignment Review</a>
            </Button>
            <Button variant="outline" asChild className="border-slate-300 rounded-md px-5 py-5 h-auto">
              <Link to="/">Back to home</Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ReportRender = ({ markdown }) => {
  if (!markdown) return null;
  const lines = markdown.split("\n");
  const elements = [];
  let para = [];
  const flush = () => {
    if (para.length) {
      elements.push(<p key={`p-${elements.length}`}>{para.join(" ")}</p>);
      para = [];
    }
  };
  lines.forEach((line, i) => {
    const s = line.trim();
    if (!s) { flush(); return; }
    if (s.startsWith("## ")) { flush(); elements.push(<h2 key={`h-${i}`}>{s.slice(3)}</h2>); }
    else if (s.startsWith("# ")) { flush(); elements.push(<h2 key={`h-${i}`}>{s.slice(2)}</h2>); }
    else para.push(s);
  });
  flush();
  return <>{elements}</>;
};

export default Results;
